import { Anchor, Breadcrumbs, Text } from "@mantine/core";
import { IconChevronRightOutline18 } from "nucleo-ui-outline-18";
import { useTranslation } from "react-i18next";

import type { SupportedLanguage } from "@mountayaapp/locales";
import { ICON_SIZE } from "@mountayaapp/theme";

import { buildDocHref, normalizeHref } from "../content/href";
import { getNavManifest, type NavGroup } from "../content/registry";

/**
 * DocsBreadcrumbsProps carry the active pathname (matched against the manifest
 * to find the owning section), the active language (selects that manifest), and
 * the current page's frontmatter title (the trailing, non-link crumb on a page
 * below its section's overview).
 */
interface DocsBreadcrumbsProps {
  lang: SupportedLanguage;
  pathname: string;
  title: string;
}

/**
 * findOwningGroup returns the manifest group whose items include the page at the
 * given pathname, or `null` when no group lists it. Comparison runs through
 * `normalizeHref` so a trailing-slash difference between the live pathname and a
 * manifest href still matches, keeping section resolution single-sourced from
 * the same manifest the sidebar and pager use.
 */
export const findOwningGroup = (groups: NavGroup[], pathname: string): NavGroup | null => {
  const current = normalizeHref(pathname);

  return (
    groups.find((group) => group.items.some((item) => normalizeHref(item.href) === current)) ?? null
  );
};

/**
 * DocsBreadcrumbs is the trail above a page's body, derived from the owning
 * section in the curated manifest. The language root carries no trail. A
 * section's overview page (the section's own first page) shows a single,
 * non-link crumb of the section name, since its frontmatter title ("Overview")
 * would only restate the section. Every page below the overview shows two
 * crumbs: the section name linking to its overview, then the current page title
 * as plain text. The section crumb is a full-reload anchor like the sidebar
 * links, since the docs app serves static pages with no client router. The trail
 * is suppressed on any page no manifest group lists.
 */
export const DocsBreadcrumbs = ({ lang, pathname, title }: DocsBreadcrumbsProps) => {
  const { t } = useTranslation("docs");

  // The language root is its own top of tree, so it carries no section trail.
  if (normalizeHref(pathname) === buildDocHref(lang, "")) {
    return null;
  }

  const group = findOwningGroup(getNavManifest(lang), pathname);
  const overviewHref = group?.items[0]?.href;

  if (!group || !overviewHref) {
    return null;
  }

  // The section overview is the section's own first page; its title is just
  // "Overview", so the section name alone is the more informative crumb and it
  // is the current page, hence plain text rather than a link to itself.
  const isOverview = normalizeHref(pathname) === normalizeHref(overviewHref);

  return (
    <Breadcrumbs
      mb="lg"
      separator={<IconChevronRightOutline18 size={ICON_SIZE.xs} />}
      separatorMargin="xs"
    >
      {isOverview ? (
        <Text c="dimmed">{t(group.labelKey)}</Text>
      ) : (
        <Anchor c="dimmed" href={overviewHref} size="sm">
          {t(group.labelKey)}
        </Anchor>
      )}
      {isOverview ? null : <Text c="dimmed">{title}</Text>}
    </Breadcrumbs>
  );
};
